import React, { useEffect, useState } from 'react'
import api from '../../../services/authService'
import { 
  Users, 
  Trash2, 
  Edit, 
  Plus, 
  UserPlus, 
  Search, 
  Upload, 
  CheckSquare, 
  Square, 
  GraduationCap, 
  Download, 
  UserCheck, 
  X
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { 
  UserGroupIcon, 
  UserPlusIcon, 
  PencilSquareIcon, 
  TrashIcon, 
  XMarkIcon, 
  ArrowDownTrayIcon, 
  DocumentArrowUpIcon, 
  CheckCircleIcon 
} from '@heroicons/react/24/outline'

const ROLES_INSTITUCION = [
  { valor: 'admin', etiqueta: 'Administrador' },
  { valor: 'jefe', etiqueta: 'Jefe de Institución' },
  { valor: 'supervisor', etiqueta: 'Supervisor' },
  { valor: 'maestro', etiqueta: 'Maestro / Docente' },
  { valor: 'alumno', etiqueta: 'Alumno' },
  { valor: 'usuario', etiqueta: 'Usuario General' },
];

interface Miembro {
  id_miembro: number
  id_usuario: number
  rol_miembro: string
  estado: string
  nombre: string
  apellido: string
  avatar?: string
  email: string
  rol_sistema: string
  turno?: string
}

interface Grupo {
  id_grupo: number
  nombre: string
  descripcion?: string
  miembros_count?: number
}

interface MiembroGrupo {
  id_grupo_miembro: number
  id_miembro: number
  nombre: string
  apellido: string
  avatar?: string
  email: string
  rol_miembro: string
}

interface ResumenInstitucion {
  vista: 'gestion'
  constitucion: {
    nombre: string
    tipo?: string
  }
  grupos: Grupo[]
  miembros: Miembro[]
  estadisticas_miembros: {
    total_miembros: number
    miembros_activos: number
    docentes: number
    directivos: number
    alumnos: number
    total_grupos: number
  }
}

const PaginaGestionMiembros: React.FC = () => {
  const [miembros, setMiembros] = useState<Miembro[]>([])
  const [grupos, setGrupos] = useState<Grupo[]>([])
  const [resumen, setResumen] = useState<ResumenInstitucion | null>(null)
  const [, setCargando] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [filtroRol, setFiltroRol] = useState<string>('todos')

  // Estado para gestión de grupos y matriculación avanzada
  const [nuevoGrupo, setNuevoGrupo] = useState({ nombre: '', descripcion: '' })
  const [grupoSeleccionado, setGrupoSeleccionado] = useState<Grupo | null>(null)
  const [miembrosGrupo, setMiembrosGrupo] = useState<MiembroGrupo[]>([])
  const [mostrarModalGrupo, setMostrarModalGrupo] = useState(false)
  const [mostrarModalEditarGrupo, setMostrarModalEditarGrupo] = useState(false)
  const [grupoAEditar, setGrupoAEditar] = useState<Grupo | null>(null)
  const [cargandoMiembrosGrupo, setCargandoMiembrosGrupo] = useState(false)
  const [miembroAAnadir, setMiembroAAnadir] = useState<string>("")
  const [busquedaMiembroGrupo, setBusquedaMiembroGrupo] = useState("")

  // Estados para selector masivo y buscador de alumnos
  const [pestanaGrupo, setPestanaGrupo] = useState<'matricular' | 'miembros'>('matricular')
  const [busquedaCandidato, setBusquedaCandidato] = useState("")
  const [filtroSoloAlumnos, setFiltroSoloAlumnos] = useState(true)
  const [seleccionadosParaMatricular, setSeleccionadosParaMatricular] = useState<number[]>([])
  const [matriculandoLote, setMatriculandoLote] = useState(false)
  const [seleccionadosParaRemover, setSeleccionadosParaRemover] = useState<number[]>([])
  const [removiendoLote, setRemoviendoLote] = useState(false)
  
  // Estado para gestión de miembros
  const [mostrarModalCrearMiembro, setMostrarModalCrearMiembro] = useState(false)
  const [nuevoMiembro, setNuevoMiembro] = useState({ nombre: '', apellido: '', email: '', password: '', rol_miembro: 'alumno', turno: '' })
  
  const [guardandoMiembro, setGuardandoMiembro] = useState(false)
  const [mostrarModalEditarMiembro, setMostrarModalEditarMiembro] = useState(false)
  const [miembroAEditar, setMiembroAEditar] = useState<Miembro & { password?: string }>({} as any)

  const [mostrarModalImportar, setMostrarModalImportar] = useState(false)
  const [archivoExcel, setArchivoExcel] = useState<File | null>(null)
  const [importandoExcel, setImportandoExcel] = useState(false)
  const [isDraggingExcel, setIsDraggingExcel] = useState(false)


  // Obtenemos el usuario logueado
  const usuarioLocal = localStorage.getItem('logixport_user') ? JSON.parse(localStorage.getItem('logixport_user')!) : null;
  // Obtener el rol exacto de la institución del miembro actual si está en la lista de miembros
  // Si no se encuentra en la lista (ej. un admin global o un jefe que acaba de entrar), intentamos deducirlo del rol de sistema
  let miRolInstitucion = miembros.find(m => m.id_usuario === usuarioLocal?.id)?.rol_miembro;
  
  if (!miRolInstitucion && usuarioLocal?.rol) {
    miRolInstitucion = usuarioLocal.rol.replace('institucion_', '');
  }
  
  // Helper para determinar si un rol tiene jerarquía menor
  const puedeGestionarRol = (rolObjetivo: string) => {
    // Si no pudimos determinar mi rol (ej: cargando), asumimos que no puede hacer nada por seguridad
    if (!miRolInstitucion) return false;

    const jerarquia = ['admin', 'jefe', 'supervisor', 'maestro', 'alumno', 'usuario'];
    const miIndice = jerarquia.indexOf(miRolInstitucion);
    const objetivoIndice = jerarquia.indexOf(rolObjetivo);
    
    // Admin puede gestionar a todos
    if (miRolInstitucion === 'admin') return true;
    
    // Si no soy admin y el objetivo es admin, no puedo
    if (rolObjetivo === 'admin') return false;
    
    // Para los demás, solo pueden gestionar roles que estén por debajo en la jerarquía (índice MAYOR o IGUAL)
    // Recuerda que en el array: admin es 0 (mayor poder), usuario es 5 (menor poder)
    // Por lo tanto, si soy jefe (1) y el objetivo es alumno (4): 1 <= 4 (Verdadero, puedo gestionarlo)
    return miIndice !== -1 && objetivoIndice !== -1 && miIndice <= objetivoIndice;
  };

  // Al abrir el modal de crear miembro, establecemos por defecto el rol más bajo que puede gestionar
  const abrirModalCrearMiembro = () => {
    // Buscar el primer rol que este usuario puede gestionar (excluyendo admin si no es admin)
    const rolPorDefecto = ROLES_INSTITUCION.filter(rol => puedeGestionarRol(rol.valor))[0]?.valor || 'alumno';
    
    setNuevoMiembro({ 
      nombre: '', 
      apellido: '', 
      email: '', 
      password: '', 
      rol_miembro: rolPorDefecto, 
      turno: '' 
    });
    setMostrarModalCrearMiembro(true);
  };

  const cargarDatos = async () => {
    setCargando(true)
    try {
      const dashboardRes = await api.get('/api/institucion/dashboard/resumen')
      setMiembros(dashboardRes.data?.miembros || [])
      setGrupos(dashboardRes.data?.grupos || [])
      setResumen(dashboardRes.data || null)
    } catch (error) {
      console.error("Error cargando datos", error)
      toast.error("Error al cargar datos")
    } finally {
      setCargando(false)
    }
  }

  const handleImportarExcel = async () => {
    if (!archivoExcel) return;
    setImportandoExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', archivoExcel);
      const response = await api.post('/api/institucion/miembros/importar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message || 'Importación completada');
      if (response.data.importados > 0) {
        toast.success(`Se importaron ${response.data.importados} miembros.`);
      }
      if (response.data.ignorados > 0) {
        toast.error(`${response.data.ignorados} correos ignorados o fallidos.`);
      }
      setMostrarModalImportar(false);
      setArchivoExcel(null);
      cargarDatos();
    } catch (error) {
      console.error(error);
      toast.error('Error al importar Excel');
    } finally {
      setImportandoExcel(false);
    }
  };

  const descargarPlantillaExcel = () => {
    const headers = ['Correo', 'Nombre', 'Apellido'];
    const ejemplo = ['alumno.ejemplo@universidad.edu', 'Juan', 'Perez'];
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ejemplo.join(',')].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'Plantilla_Importacion_Alumnos_Logixport.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Plantilla CSV descargada con éxito');
  };

  useEffect(() => {
    cargarDatos()
  }, [])

  const crearGrupo = async () => {
    if (!nuevoGrupo.nombre.trim()) {
      toast.error("El nombre del grupo es requerido")
      return
    }
    try {
      await api.post('/api/institucion/grupos', {
        nombre: nuevoGrupo.nombre.trim(),
        descripcion: nuevoGrupo.descripcion?.trim() || undefined,
      })
      setNuevoGrupo({ nombre: '', descripcion: '' })
      toast.success("Grupo creado exitosamente")
      await cargarDatos()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al crear grupo")
    }
  }

  const eliminarGrupo = async (id_grupo: number) => {
    if (!window.confirm("¿Estás seguro de eliminar este grupo?")) return
    try {
      await api.delete(`/api/institucion/grupos/${id_grupo}`)
      toast.success("Grupo eliminado")
      cargarDatos()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al eliminar grupo")
    }
  }

  const prepararEdicionGrupo = (grupo: Grupo) => {
    setGrupoAEditar({ ...grupo })
    setMostrarModalEditarGrupo(true)
  }

  const guardarEdicionGrupo = async () => {
    if (!grupoAEditar || !grupoAEditar.nombre.trim()) return
    try {
      await api.put(`/api/institucion/grupos/${grupoAEditar.id_grupo}`, {
        nombre: grupoAEditar.nombre,
        descripcion: grupoAEditar.descripcion
      })
      toast.success("Grupo actualizado")
      setMostrarModalEditarGrupo(false)
      setGrupoAEditar(null)
      cargarDatos()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al actualizar grupo")
    }
  }

  const abrirGestionGrupo = async (grupo: Grupo) => {
    setGrupoSeleccionado(grupo)
    setMostrarModalGrupo(true)
    setPestanaGrupo('matricular')
    setSeleccionadosParaMatricular([])
    setSeleccionadosParaRemover([])
    setBusquedaCandidato('')
    setBusquedaMiembroGrupo('')
    setCargandoMiembrosGrupo(true)
    try {
      const res = await api.get(`/api/institucion/grupos/${grupo.id_grupo}/miembros`)
      setMiembrosGrupo(res.data || [])
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al cargar miembros del grupo")
    } finally {
      setCargandoMiembrosGrupo(false)
    }
  }

  const cerrarGestionGrupo = () => {
    setGrupoSeleccionado(null)
    setMostrarModalGrupo(false)
    setMiembrosGrupo([])
    setSeleccionadosParaMatricular([])
    setSeleccionadosParaRemover([])
    setBusquedaCandidato("")
    setBusquedaMiembroGrupo("")
  }

  const eliminarMiembroDeGrupo = async (id_miembro: number) => {
    if (!grupoSeleccionado) return
    try {
      await api.delete(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros/${id_miembro}`)
      toast.success("Miembro eliminado del grupo")
      const res = await api.get(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros`)
      setMiembrosGrupo(res.data || [])
      await cargarDatos()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al eliminar miembro")
    }
  }

  const anadirMiembroAGrupo = async (id_miembro_directo?: number) => {
    if (!grupoSeleccionado) return
    const id = id_miembro_directo || (miembroAAnadir ? parseInt(miembroAAnadir) : 0)
    if (!id) return
    try {
      await api.post(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros`, {
        id_miembro: id
      })
      toast.success("Alumno matriculado con éxito")
      setMiembroAAnadir("")
      setSeleccionadosParaMatricular(prev => prev.filter(item => item !== id))
      const res = await api.get(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros`)
      setMiembrosGrupo(res.data || [])
      await cargarDatos()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al añadir miembro")
    }
  }

  const matricularSeleccionados = async () => {
    if (!grupoSeleccionado || seleccionadosParaMatricular.length === 0) return
    setMatriculandoLote(true)
    try {
      await api.post(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros`, {
        ids_miembros: seleccionadosParaMatricular
      })
      toast.success(`¡${seleccionadosParaMatricular.length} alumno(s) matriculado(s) exitosamente!`)
      setSeleccionadosParaMatricular([])
      const res = await api.get(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros`)
      setMiembrosGrupo(res.data || [])
      await cargarDatos()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al matricular alumnos en lote")
    } finally {
      setMatriculandoLote(false)
    }
  }

  const removerSeleccionadosDelGrupo = async () => {
    if (!grupoSeleccionado || seleccionadosParaRemover.length === 0) return
    if (!window.confirm(`¿Estás seguro de remover ${seleccionadosParaRemover.length} alumnos de este grupo?`)) return
    setRemoviendoLote(true)
    try {
      await Promise.all(
        seleccionadosParaRemover.map(id =>
          api.delete(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros/${id}`)
        )
      )
      toast.success(`${seleccionadosParaRemover.length} alumno(s) removido(s) del grupo`)
      setSeleccionadosParaRemover([])
      const res = await api.get(`/api/institucion/grupos/${grupoSeleccionado.id_grupo}/miembros`)
      setMiembrosGrupo(res.data || [])
      await cargarDatos()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.detail || "Error al remover alumnos")
    } finally {
      setRemoviendoLote(false)
    }
  }

  const exportarListaGrupoCSV = () => {
    if (!grupoSeleccionado || miembrosGrupo.length === 0) {
      toast.error("No hay alumnos inscritos en este grupo para exportar")
      return
    }
    const headers = ['Nombre', 'Apellido', 'Correo', 'Rol_Institucional', 'Grupo']
    const filas = miembrosGrupo.map(m => [
      `"${m.nombre || ''}"`,
      `"${m.apellido || ''}"`,
      `"${m.email || ''}"`,
      `"${m.rol_miembro || 'alumno'}"`,
      `"${grupoSeleccionado.nombre}"`
    ])
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...filas.map(f => f.join(','))].join('\n')
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', `Lista_Alumnos_${grupoSeleccionado.nombre.replace(/\s+/g, '_')}_Logixport.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('Lista oficial de alumnos descargada')
  }

  const alternarSeleccionCandidato = (id_miembro: number) => {
    setSeleccionadosParaMatricular(prev =>
      prev.includes(id_miembro) ? prev.filter(id => id !== id_miembro) : [...prev, id_miembro]
    )
  }

  const alternarSeleccionRemover = (id_miembro: number) => {
    setSeleccionadosParaRemover(prev =>
      prev.includes(id_miembro) ? prev.filter(id => id !== id_miembro) : [...prev, id_miembro]
    )
  }

  // Candidatos disponibles en la institución para matricular
  const candidatosDisponibles = miembros.filter(m => {
    const yaEnGrupo = miembrosGrupo.some(mg => mg.id_miembro === m.id_miembro)
    if (yaEnGrupo) return false
    if (filtroSoloAlumnos && m.rol_miembro !== 'alumno') return false
    if (!busquedaCandidato.trim()) return true
    const q = busquedaCandidato.toLowerCase().trim()
    const nombreCompleto = `${m.nombre} ${m.apellido}`.toLowerCase()
    return nombreCompleto.includes(q) || (m.email || '').toLowerCase().includes(q)
  })

  const todosCandidatosVisiblesSeleccionados =
    candidatosDisponibles.length > 0 &&
    candidatosDisponibles.every(c => seleccionadosParaMatricular.includes(c.id_miembro))

  const alternarSeleccionarTodosCandidatos = () => {
    const idsVisibles = candidatosDisponibles.map(c => c.id_miembro)
    if (todosCandidatosVisiblesSeleccionados) {
      setSeleccionadosParaMatricular(prev => prev.filter(id => !idsVisibles.includes(id)))
    } else {
      setSeleccionadosParaMatricular(prev => Array.from(new Set([...prev, ...idsVisibles])))
    }
  }

  // Miembros filtrados dentro del grupo
  const miembrosGrupoFiltrados = miembrosGrupo.filter(mg => {
    const q = (busquedaMiembroGrupo || '').toLowerCase().trim()
    if (!q) return true
    return (
      (mg.nombre || '').toLowerCase().includes(q) ||
      (mg.apellido || '').toLowerCase().includes(q) ||
      (mg.email || '').toLowerCase().includes(q)
    )
  })

  const todosMiembrosGrupoVisiblesSeleccionados =
    miembrosGrupoFiltrados.length > 0 &&
    miembrosGrupoFiltrados.every(mg => seleccionadosParaRemover.includes(mg.id_miembro))

  const alternarSeleccionarTodosRemover = () => {
    const idsVisibles = miembrosGrupoFiltrados.map(mg => mg.id_miembro)
    if (todosMiembrosGrupoVisiblesSeleccionados) {
      setSeleccionadosParaRemover(prev => prev.filter(id => !idsVisibles.includes(id)))
    } else {
      setSeleccionadosParaRemover(prev => Array.from(new Set([...prev, ...idsVisibles])))
    }
  }

  const prepararEdicionMiembro = (miembro: Miembro) => {
    // Es crítico clonar el objeto para no mutar el estado directamente
    setMiembroAEditar({ 
      ...miembro, 
      password: '',
      turno: miembro.turno || '' 
    })
    setMostrarModalEditarMiembro(true)
  }

  const guardarEdicionMiembro = async () => {
    if (!miembroAEditar || !miembroAEditar.nombre || !miembroAEditar.apellido || !miembroAEditar.email) {
      toast.error("El nombre, apellido y email son requeridos")
      return
    }
    try {
      const data: any = {
        nombre: miembroAEditar.nombre,
        apellido: miembroAEditar.apellido,
        email: miembroAEditar.email,
        rol_miembro: miembroAEditar.rol_miembro,
        estado: miembroAEditar.estado,
      }
      
      data.turno = miembroAEditar.turno && miembroAEditar.turno.trim() ? miembroAEditar.turno : null;
      if (miembroAEditar.password) {
        data.password = miembroAEditar.password
      }
      
      await api.put(`/api/institucion/miembros/${miembroAEditar.id_miembro}`, data)
      toast.success("Miembro actualizado exitosamente")
      setMostrarModalEditarMiembro(false)
      cargarDatos()
    } catch (error: any) {
      console.error(error)
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error("Error al actualizar miembro")
      }
    }
  }

  const crearMiembro = async () => {
    if (!nuevoMiembro.nombre || !nuevoMiembro.apellido || !nuevoMiembro.email || !nuevoMiembro.password) {
      toast.error("Por favor completa todos los campos obligatorios")
      return
    }
    
    setGuardandoMiembro(true)
    try {
      const payload = {
        ...nuevoMiembro,
        turno: nuevoMiembro.turno && nuevoMiembro.turno.trim() ? nuevoMiembro.turno : null,
      };
      await api.post('/api/institucion/miembros', payload)
      toast.success("Miembro creado exitosamente")
      setMostrarModalCrearMiembro(false)
      setNuevoMiembro({ nombre: '', apellido: '', email: '', password: '', rol_miembro: 'alumno', turno: '' })
      cargarDatos()
    } catch (error: any) {
      console.error(error)
      const detalle = error.response?.data?.detail;
      if (detalle === "El correo electrónico ya está registrado") {
        toast.error("Este correo ya está registrado en el sistema");
      } else if (typeof detalle === "string" && detalle.trim()) {
        toast.error(detalle);
      } else {
        toast.error("Error al crear miembro");
      }
    } finally {
      setGuardandoMiembro(false)
    }
  }

  const eliminarMiembro = async (id_miembro: number) => {
    if (!window.confirm("¿Estás seguro de eliminar este miembro? Se eliminará su acceso y asignaciones a grupos de forma permanente.")) return
    try {
      await api.delete(`/api/institucion/miembros/${id_miembro}`)
      toast.success("Miembro eliminado")
      cargarDatos()
    } catch (error: any) {
      console.error(error)
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error("Error al eliminar miembro")
      }
    }
  }

  // Filtrado de miembros
  const miembrosFiltrados = miembros.filter(m => {
    const terminoBusqueda = (busqueda || '').toLowerCase()
    const cumpleBusqueda = 
      (m.nombre || '').toLowerCase().includes(terminoBusqueda) || 
      (m.apellido || '').toLowerCase().includes(terminoBusqueda) ||
      (m.email || '').toLowerCase().includes(terminoBusqueda)
    
    if (!cumpleBusqueda) return false

    if (filtroRol === 'todos') return true
    if (filtroRol === 'directivos') return ['admin', 'jefe', 'supervisor'].includes(m.rol_miembro)
    if (filtroRol === 'maestros') return m.rol_miembro === 'maestro'
    if (filtroRol === 'estudiantes') return ['alumno', 'usuario'].includes(m.rol_miembro)
    
    return true
  })

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestión Institucional</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {resumen?.constitucion?.nombre
              ? `Administra los miembros y grupos de ${resumen.constitucion.nombre}`
              : 'Administra los miembros y grupos de la institución'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 backdrop-blur-sm dark:bg-gray-800/50 p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Miembros activos</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{resumen?.estadisticas_miembros.miembros_activos ?? miembros.filter(m => m.estado === 'activo').length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 backdrop-blur-sm dark:bg-gray-800/50 p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Docentes</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{resumen?.estadisticas_miembros.docentes ?? miembros.filter(m => m.rol_miembro === 'maestro').length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 backdrop-blur-sm dark:bg-gray-800/50 p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Alumnos</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{resumen?.estadisticas_miembros.alumnos ?? miembros.filter(m => ['alumno', 'usuario'].includes(m.rol_miembro)).length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 backdrop-blur-sm dark:bg-gray-800/50 p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Grupos</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{resumen?.estadisticas_miembros.total_grupos ?? grupos.length}</p>
        </div>
      </div>

      {/* Sección de Miembros */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-200/60 dark:border-gray-700/50 overflow-hidden">
        <div className="p-6 border-b border-gray-200/60 dark:border-gray-700/50">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg text-blue-600 dark:text-blue-400">
                <Users className="h-5 w-5" />
              </div>
              Directorio de Miembros
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                <label htmlFor="institucion-miembros-busqueda" className="sr-only">Buscar miembro</label>
                <input 
                  id="institucion-miembros-busqueda"
                  name="busquedaMiembros"
                  type="text" 
                  placeholder="Buscar miembro..." 
                  className="pl-9 pr-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-blue-500/50 outline-none w-full sm:w-64 transition-all shadow-sm"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="institucion-miembros-filtro-rol" className="sr-only">Filtrar por rol</label>
                <select 
                  id="institucion-miembros-filtro-rol"
                  name="filtroRol"
                  className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-blue-500/50 outline-none shadow-sm font-medium"
                  value={filtroRol}
                  onChange={(e) => setFiltroRol(e.target.value)}
                >
                  <option value="todos">Todos los roles</option>
                  <option value="directivos">Directivos</option>
                  <option value="maestros">Maestros</option>
                  <option value="estudiantes">Estudiantes</option>
                </select>
              </div>

              <button 
                onClick={abrirModalCrearMiembro}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm font-medium whitespace-nowrap"
              >
                <UserPlus size={18} />
                Nuevo Miembro
              </button>
              <button 
                onClick={() => setMostrarModalImportar(true)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm font-medium whitespace-nowrap"
              >
                <Upload size={18} />
                Importar
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-[11px] text-gray-500 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-900/50 dark:text-gray-400 border-b border-gray-200/60 dark:border-gray-700/50">
              <tr>
                <th className="px-6 py-4 font-semibold">Nombre</th>
                <th className="px-6 py-4 font-semibold">Email</th>
                <th className="px-6 py-4 font-semibold">Rol</th>
                <th className="px-6 py-4 font-semibold">Turno</th>
                <th className="px-6 py-4 font-semibold">Estado</th>
                <th className="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800/50">
              {miembrosFiltrados.length > 0 ? (
                miembrosFiltrados.map((miembro) => (
                  <tr key={miembro.id_miembro} className="bg-transparent hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center gap-3">
                        {miembro.avatar ? (
                          <img src={miembro.avatar} alt={`${miembro.nombre} ${miembro.apellido}`} className="h-8 w-8 rounded-full object-cover shadow-sm border border-slate-200 dark:border-white/10" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-xs shadow-inner">
                            {(miembro.nombre || ' ').charAt(0)}{(miembro.apellido || ' ').charAt(0)}
                          </div>
                        )}
                        {miembro.nombre} {miembro.apellido}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                      {miembro.email}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider
                        ${['admin', 'jefe', 'supervisor'].includes(miembro.rol_miembro) ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20' : 
                          miembro.rol_miembro === 'maestro' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20' : 
                          'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'}`}>
                        {ROLES_INSTITUCION.find(r => r.valor === miembro.rol_miembro)?.etiqueta || miembro.rol_miembro}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                      {miembro.turno ? (
                        <span className="capitalize">{miembro.turno}</span>
                      ) : (
                        <span className="text-gray-400 italic">Sin asignar</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <div className={`h-2 w-2 rounded-full ${miembro.estado === 'activo' ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300 capitalize">{miembro.estado}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => prepararEdicionMiembro(miembro)}
                          className={`p-1.5 rounded-lg transition-colors ${!puedeGestionarRol(miembro.rol_miembro) && miembro.id_usuario !== usuarioLocal?.id ? 'text-gray-300 cursor-not-allowed opacity-50' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10'}`}
                          title={!puedeGestionarRol(miembro.rol_miembro) && miembro.id_usuario !== usuarioLocal?.id ? 'No tienes permisos de jerarquía para editar este rol' : 'Editar miembro'}
                          disabled={!puedeGestionarRol(miembro.rol_miembro) && miembro.id_usuario !== usuarioLocal?.id}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => eliminarMiembro(miembro.id_miembro)}
                          className={`p-1.5 rounded-lg transition-colors ${!puedeGestionarRol(miembro.rol_miembro) || miembro.id_usuario === usuarioLocal?.id ? 'text-gray-300 cursor-not-allowed opacity-50' : 'text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10'}`}
                          title={miembro.id_usuario === usuarioLocal?.id ? 'No puedes eliminarte a ti mismo' : !puedeGestionarRol(miembro.rol_miembro) ? 'No tienes permisos de jerarquía para eliminar este rol' : 'Eliminar miembro'}
                          disabled={!puedeGestionarRol(miembro.rol_miembro) || miembro.id_usuario === usuarioLocal?.id}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                      <Users className="h-12 w-12 mb-3 opacity-20" />
                      <p>No se encontraron miembros que coincidan con la búsqueda.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Editar Miembro (Bento Glassmorphic) */}
      {mostrarModalEditarMiembro && miembroAEditar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fadeIn">
          <div className="bg-white/95 dark:bg-[#0c0c0c]/95 rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-2xl w-full max-w-lg p-6 sm:p-8 overflow-hidden relative">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                  <PencilSquareIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">Editar Miembro</h3>
                  <p className="text-xs text-slate-400 font-medium">Actualiza rol, credenciales y turno operativo</p>
                </div>
              </div>
              <button 
                onClick={() => setMostrarModalEditarMiembro(false)} 
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Contexto del Miembro */}
            <div className="mt-4 p-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-sm">
                  {(miembroAEditar.nombre || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-black text-slate-900 dark:text-white">
                    {miembroAEditar.nombre} {miembroAEditar.apellido}
                  </p>
                  <p className="text-[11px] text-slate-400 font-mono">{miembroAEditar.email}</p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase font-mono bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-500/20">
                {miembroAEditar.rol_miembro}
              </span>
            </div>
            
            <div className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="institucion-editar-miembro-nombre" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Nombre *</label>
                  <input 
                    id="institucion-editar-miembro-nombre"
                    name="miembroAEditar.nombre"
                    type="text" 
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                    value={miembroAEditar.nombre}
                    onChange={(e) => setMiembroAEditar({...miembroAEditar, nombre: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="institucion-editar-miembro-apellido" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Apellido *</label>
                  <input 
                    id="institucion-editar-miembro-apellido"
                    name="miembroAEditar.apellido"
                    type="text" 
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                    value={miembroAEditar.apellido}
                    onChange={(e) => setMiembroAEditar({...miembroAEditar, apellido: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="institucion-editar-miembro-email" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Email Institucional *</label>
                <input 
                  id="institucion-editar-miembro-email"
                  name="miembroAEditar.email"
                  type="email" 
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  value={miembroAEditar.email}
                  onChange={(e) => setMiembroAEditar({...miembroAEditar, email: e.target.value})}
                />
              </div>

              <div>
                <label htmlFor="institucion-editar-miembro-password" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">
                  Nueva Contraseña <span className="text-slate-400 font-normal normal-case">(Opcional)</span>
                </label>
                <input 
                  id="institucion-editar-miembro-password"
                  name="miembroAEditar.password"
                  type="password" 
                  placeholder="Dejar en blanco para mantener la actual"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  value={miembroAEditar.password || ''}
                  onChange={(e) => setMiembroAEditar({...miembroAEditar, password: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="institucion-editar-miembro-rol" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Rol</label>
                  <select
                    id="institucion-editar-miembro-rol"
                    name="miembroAEditar.rol_miembro"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#141414] text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    value={miembroAEditar.rol_miembro}
                    onChange={(e) => setMiembroAEditar({...miembroAEditar, rol_miembro: e.target.value as any})}
                    disabled={!puedeGestionarRol(miembroAEditar.rol_miembro)}
                  >
                    {ROLES_INSTITUCION.filter(rol => puedeGestionarRol(rol.valor)).map(rol => (
                      <option key={rol.valor} value={rol.valor}>{rol.etiqueta}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="institucion-editar-miembro-estado" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Estado</label>
                  <select
                    id="institucion-editar-miembro-estado"
                    name="miembroAEditar.estado"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#141414] text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    value={miembroAEditar.estado}
                    onChange={(e) => setMiembroAEditar({...miembroAEditar, estado: e.target.value})}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="institucion-editar-miembro-turno" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Turno Académico / Logístico</label>
                <select
                  id="institucion-editar-miembro-turno"
                  name="miembroAEditar.turno"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#141414] text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                  value={miembroAEditar.turno || ''}
                  onChange={(e) => setMiembroAEditar({...miembroAEditar, turno: e.target.value})}
                >
                  <option value="">Seleccionar turno...</option>
                  <option value="matutino">Matutino</option>
                  <option value="vespertino">Vespertino</option>
                  <option value="nocturno">Nocturno</option>
                  <option value="mixto">Mixto</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-white/[0.06]">
                <button 
                  onClick={() => setMostrarModalEditarMiembro(false)}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                >
                  Cancelar
                </button>
                <button 
                  onClick={guardarEdicionMiembro}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md shadow-blue-500/25 transition-all"
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Miembro (Bento Glassmorphic) */}
      {mostrarModalCrearMiembro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fadeIn">
          <div className="bg-white/95 dark:bg-[#0c0c0c]/95 rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-2xl w-full max-w-lg p-6 sm:p-8 overflow-hidden relative">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                  <UserPlusIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">Alta de Nuevo Miembro</h3>
                  <p className="text-xs text-slate-400 font-medium">Registra alumnos, docentes o directivos</p>
                </div>
              </div>
              <button 
                onClick={() => setMostrarModalCrearMiembro(false)} 
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="institucion-crear-miembro-nombre" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Nombre *</label>
                  <input 
                    id="institucion-crear-miembro-nombre"
                    name="nuevoMiembro.nombre"
                    type="text" 
                    placeholder="Ej. Carlos"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                    value={nuevoMiembro.nombre}
                    onChange={(e) => setNuevoMiembro({...nuevoMiembro, nombre: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="institucion-crear-miembro-apellido" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Apellido *</label>
                  <input 
                    id="institucion-crear-miembro-apellido"
                    name="nuevoMiembro.apellido"
                    type="text" 
                    placeholder="Ej. Mendoza"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                    value={nuevoMiembro.apellido}
                    onChange={(e) => setNuevoMiembro({...nuevoMiembro, apellido: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="institucion-crear-miembro-email" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Email Institucional *</label>
                <input 
                  id="institucion-crear-miembro-email"
                  name="nuevoMiembro.email"
                  type="email" 
                  placeholder="correo@institucion.edu"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  value={nuevoMiembro.email}
                  onChange={(e) => setNuevoMiembro({...nuevoMiembro, email: e.target.value})}
                />
              </div>

              <div>
                <label htmlFor="institucion-crear-miembro-password" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Contraseña Temporal *</label>
                <input 
                  id="institucion-crear-miembro-password"
                  name="nuevoMiembro.password"
                  type="password" 
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  value={nuevoMiembro.password}
                  onChange={(e) => setNuevoMiembro({...nuevoMiembro, password: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="institucion-crear-miembro-rol" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Rol *</label>
                  <select
                    id="institucion-crear-miembro-rol"
                    name="nuevoMiembro.rol_miembro"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#141414] text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    value={nuevoMiembro.rol_miembro}
                    onChange={(e) => setNuevoMiembro({...nuevoMiembro, rol_miembro: e.target.value})}
                  >
                    {ROLES_INSTITUCION.filter(rol => puedeGestionarRol(rol.valor)).map(rol => (
                      <option key={rol.valor} value={rol.valor}>{rol.etiqueta}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="institucion-crear-miembro-turno" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Turno</label>
                  <select
                    id="institucion-crear-miembro-turno"
                    name="nuevoMiembro.turno"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#141414] text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    value={nuevoMiembro.turno}
                    onChange={(e) => setNuevoMiembro({...nuevoMiembro, turno: e.target.value})}
                  >
                    <option value="">Seleccionar turno...</option>
                    <option value="matutino">Matutino</option>
                    <option value="vespertino">Vespertino</option>
                    <option value="nocturno">Nocturno</option>
                    <option value="mixto">Mixto</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-white/[0.06]">
                <button 
                  onClick={() => setMostrarModalCrearMiembro(false)}
                  disabled={guardandoMiembro}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-all disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button 
                  onClick={crearMiembro}
                  disabled={guardandoMiembro}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md shadow-blue-500/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {guardandoMiembro ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/20 border-t-white" />
                      <span>Creando...</span>
                    </>
                  ) : (
                    <span>Registrar Miembro</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sección de Grupos */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-200/60 dark:border-gray-700/50 p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-500/10 rounded-lg text-indigo-600 dark:text-indigo-400">
              <UserGroupIcon className="h-6 w-6" />
            </div>
            Grupos de Trabajo
          </h2>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-8 bg-gray-50/50 dark:bg-gray-900/30 p-4 rounded-2xl border border-gray-100 dark:border-gray-700/50">
          <div className="flex-1">
            <label htmlFor="institucion-nuevo-grupo-nombre" className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wider">Nuevo Grupo</label>
            <input 
              id="institucion-nuevo-grupo-nombre"
              name="nuevoGrupo.nombre"
              type="text" 
              placeholder="Nombre del grupo (ej. 1° A)" 
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none shadow-sm"
              value={nuevoGrupo.nombre}
              onChange={(e) => setNuevoGrupo({...nuevoGrupo, nombre: e.target.value})}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="institucion-nuevo-grupo-descripcion" className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wider">Descripción</label>
            <input 
              id="institucion-nuevo-grupo-descripcion"
              name="nuevoGrupo.descripcion"
              type="text" 
              placeholder="Descripción opcional" 
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none shadow-sm"
              value={nuevoGrupo.descripcion}
              onChange={(e) => setNuevoGrupo({...nuevoGrupo, descripcion: e.target.value})}
            />
          </div>
          <div className="flex items-end">
            <button 
              onClick={crearGrupo}
              className="w-full md:w-auto px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm font-medium"
            >
              <Plus size={18} />
              Crear Grupo
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {grupos.map(grupo => {
            const numMiembros = grupo.miembros_count ?? 0
            const porcentajeCapacidad = Math.min(100, Math.round((numMiembros / 30) * 100))
            return (
              <div 
                key={grupo.id_grupo} 
                className="p-5 rounded-2xl border border-slate-200/80 dark:border-white/[0.08] bg-white/90 dark:bg-white/[0.02] hover:border-indigo-400 dark:hover:border-indigo-500/50 transition-all hover:shadow-xl flex flex-col justify-between group"
              >
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                        <GraduationCap size={18} />
                      </div>
                      <h3 className="font-black text-slate-900 dark:text-white text-base tracking-tight">{grupo.nombre}</h3>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => prepararEdicionGrupo(grupo)} 
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-lg transition-colors cursor-pointer"
                        title="Editar grupo"
                      >
                        <Edit size={15} />
                      </button>
                      <button 
                        onClick={() => eliminarGrupo(grupo.id_grupo)} 
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors cursor-pointer"
                        title="Eliminar grupo"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 dark:text-zinc-400 mb-4 h-9 overflow-hidden text-ellipsis leading-relaxed">
                    {grupo.descripcion || "Sin descripción asignada"}
                  </p>

                  {/* Barra de Progreso de Alumnos en el Salón */}
                  <div className="mb-4 p-2.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.03]">
                    <div className="flex items-center justify-between text-[11px] font-bold mb-1.5">
                      <span className="text-slate-500 dark:text-zinc-400">Ocupación del Salón:</span>
                      <span className="font-mono text-indigo-600 dark:text-indigo-400">{numMiembros} / 30 alumnos</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          numMiembros >= 30 ? 'bg-amber-500' : 'bg-gradient-to-r from-indigo-500 to-blue-500'
                        }`}
                        style={{ width: `${Math.max(5, porcentajeCapacidad)}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-white/[0.04] mt-auto">
                  <div className="flex items-center gap-1.5 text-slate-500 dark:text-zinc-400 text-xs font-semibold">
                    <Users size={14} className="text-indigo-500" />
                    <span>{numMiembros} matriculados</span>
                  </div>
                  
                  <button 
                    onClick={() => abrirGestionGrupo(grupo)}
                    className="px-3.5 py-1.5 text-xs font-bold text-indigo-600 hover:text-white dark:text-indigo-400 dark:hover:text-white bg-indigo-50 hover:bg-indigo-600 dark:bg-indigo-500/10 dark:hover:bg-indigo-600 rounded-xl transition-all flex items-center gap-1.5 shadow-sm active:scale-95 cursor-pointer"
                  >
                    <UserPlus size={14} />
                    <span>Control Salón</span>
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Modal Gestión de Miembros de Grupo (Centro de Control de Grupos / Salones 4.0) */}
      {mostrarModalGrupo && grupoSeleccionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-md animate-fadeIn">
          <div className="bg-white/95 dark:bg-[#0c0c0c]/95 rounded-[32px] border border-slate-200/80 dark:border-white/[0.08] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col relative transition-all">
            
            {/* Header del Centro de Control */}
            <div className="p-6 border-b border-slate-100 dark:border-white/[0.06] bg-slate-50/60 dark:bg-white/[0.02]">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="p-3 rounded-2xl bg-gradient-to-tr from-indigo-500/20 to-blue-500/20 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 shadow-sm">
                    <GraduationCap className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                        {grupoSeleccionado.nombre}
                      </h3>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase font-mono bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
                        Salón / Grupo Académico
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium mt-0.5">
                      {grupoSeleccionado.descripcion || "Gestión de alumnos, matrícula y control de asistencia"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center">
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-slate-100 dark:bg-white/5 border border-slate-200/60 dark:border-white/[0.06]">
                    <span className="text-[11px] font-bold text-slate-700 dark:text-zinc-300">
                      Capacidad:
                    </span>
                    <span className="text-[11px] font-mono font-black text-indigo-600 dark:text-indigo-400">
                      {miembrosGrupo.length} / 30 alumnos
                    </span>
                  </div>
                  <button 
                    onClick={cerrarGestionGrupo} 
                    className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Pestañas de Navegación del Salón */}
              <div className="flex items-center gap-2 mt-5 border-b border-slate-200/60 dark:border-white/[0.06] -mb-6 pb-0">
                <button
                  type="button"
                  onClick={() => setPestanaGrupo('matricular')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-xs font-black rounded-t-xl transition-all border-b-2 ${
                    pestanaGrupo === 'matricular'
                      ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white/80 dark:bg-white/5'
                      : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300'
                  }`}
                >
                  <UserPlus size={15} />
                  <span>Matricular Alumnos</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-mono">
                    {candidatosDisponibles.length} disponibles
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setPestanaGrupo('miembros')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-xs font-black rounded-t-xl transition-all border-b-2 ${
                    pestanaGrupo === 'miembros'
                      ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white/80 dark:bg-white/5'
                      : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300'
                  }`}
                >
                  <Users size={15} />
                  <span>Alumnos Inscritos</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-zinc-300 font-mono">
                    {miembrosGrupo.length}
                  </span>
                </button>
              </div>
            </div>

            {/* Contenedor del Modal con Tabs */}
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              
              {/* PESTAÑA 1: MATRICULAR ALUMNOS (BUSCADOR Y SELECCIÓN MÚLTIPLE) */}
              {pestanaGrupo === 'matricular' && (
                <div className="space-y-4 animate-fadeIn">
                  
                  {/* Buscador y Filtros */}
                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05] space-y-3">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                      
                      {/* Input de Búsqueda en Vivo */}
                      <div className="relative flex-1">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                        <input
                          type="text"
                          placeholder="Buscar alumno por nombre, apellido o correo institucional..."
                          className="w-full pl-10 pr-9 py-2.5 rounded-xl text-xs font-medium bg-white dark:bg-[#141414] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500/30 outline-none transition-all"
                          value={busquedaCandidato}
                          onChange={(e) => setBusquedaCandidato(e.target.value)}
                        />
                        {busquedaCandidato && (
                          <button
                            type="button"
                            onClick={() => setBusquedaCandidato("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Toggle de Roles */}
                      <div className="flex items-center gap-1.5 p-1 bg-slate-200/50 dark:bg-white/5 rounded-xl">
                        <button
                          type="button"
                          onClick={() => setFiltroSoloAlumnos(true)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            filtroSoloAlumnos 
                              ? 'bg-white dark:bg-[#1c1c1c] text-indigo-600 dark:text-indigo-400 shadow-sm' 
                              : 'text-slate-500 hover:text-slate-700 dark:text-zinc-400'
                          }`}
                        >
                          Solo Alumnos
                        </button>
                        <button
                          type="button"
                          onClick={() => setFiltroSoloAlumnos(false)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            !filtroSoloAlumnos 
                              ? 'bg-white dark:bg-[#1c1c1c] text-indigo-600 dark:text-indigo-400 shadow-sm' 
                              : 'text-slate-500 hover:text-slate-700 dark:text-zinc-400'
                          }`}
                        >
                          Todos los Miembros
                        </button>
                      </div>
                    </div>

                    {/* Barra de Acciones de Selección Múltiple */}
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-200/60 dark:border-white/[0.05]">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={alternarSeleccionarTodosCandidatos}
                          disabled={candidatosDisponibles.length === 0}
                          className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-zinc-300 hover:text-indigo-600 transition-colors disabled:opacity-40 cursor-pointer"
                        >
                          {todosCandidatosVisiblesSeleccionados ? (
                            <CheckSquare className="h-4 w-4 text-indigo-600" />
                          ) : (
                            <Square className="h-4 w-4 text-slate-400" />
                          )}
                          <span>Seleccionar todos ({candidatosDisponibles.length})</span>
                        </button>

                        {seleccionadosParaMatricular.length > 0 && (
                          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 font-mono">
                            {seleccionadosParaMatricular.length} seleccionados
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {seleccionadosParaMatricular.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSeleccionadosParaMatricular([])}
                            className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-white transition-colors"
                          >
                            Limpiar
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={matricularSeleccionados}
                          disabled={seleccionadosParaMatricular.length === 0 || matriculandoLote}
                          className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-black rounded-xl flex items-center gap-2 shadow-md shadow-indigo-500/25 transition-all active:scale-95 cursor-pointer"
                        >
                          {matriculandoLote ? (
                            <>
                              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/20 border-t-white" />
                              <span>Matriculando...</span>
                            </>
                          ) : (
                            <>
                              <UserCheck size={15} />
                              <span>Matricular Selección ({seleccionadosParaMatricular.length})</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Listado de Candidatos Disponibles */}
                  <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                    {candidatosDisponibles.map(candidato => {
                      const estaSeleccionado = seleccionadosParaMatricular.includes(candidato.id_miembro)
                      return (
                        <div
                          key={candidato.id_miembro}
                          onClick={() => alternarSeleccionCandidato(candidato.id_miembro)}
                          className={`flex items-center justify-between p-3 rounded-2xl border transition-all cursor-pointer ${
                            estaSeleccionado
                              ? 'bg-indigo-50/80 dark:bg-indigo-950/20 border-indigo-400 dark:border-indigo-500/40 shadow-sm'
                              : 'bg-white dark:bg-white/[0.02] hover:bg-slate-50 dark:hover:bg-white/[0.04] border-slate-200/70 dark:border-white/[0.05]'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              onClick={(e) => { e.stopPropagation(); alternarSeleccionCandidato(candidato.id_miembro); }}
                              className="text-indigo-600"
                            >
                              {estaSeleccionado ? (
                                <CheckSquare className="h-4 w-4" />
                              ) : (
                                <Square className="h-4 w-4 text-slate-300 dark:text-zinc-600" />
                              )}
                            </div>

                            {candidato.avatar ? (
                              <img 
                                src={candidato.avatar} 
                                alt={`${candidato.nombre} ${candidato.apellido}`} 
                                className="w-8 h-8 rounded-full object-cover shadow-sm border border-slate-200 dark:border-white/10" 
                                referrerPolicy="no-referrer" 
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-500 text-white font-black text-xs flex items-center justify-center shadow-sm">
                                {(candidato.nombre || 'A').charAt(0).toUpperCase()}
                              </div>
                            )}

                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-bold text-slate-900 dark:text-white">
                                  {candidato.nombre} {candidato.apellido}
                                </p>
                                <span className="px-2 py-0.2 rounded-md text-[10px] font-black uppercase font-mono bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10">
                                  {candidato.rol_miembro}
                                </span>
                                {candidato.turno && (
                                  <span className="px-1.5 py-0.2 rounded-md text-[9px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10">
                                    {candidato.turno}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-400 font-mono mt-0.5">{candidato.email}</p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              anadirMiembroAGrupo(candidato.id_miembro);
                            }}
                            className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-600 hover:text-white dark:bg-white/5 dark:hover:bg-indigo-600 text-slate-700 dark:text-zinc-300 text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm active:scale-95 cursor-pointer"
                          >
                            <Plus size={14} />
                            <span>Matricular</span>
                          </button>
                        </div>
                      )
                    })}

                    {candidatosDisponibles.length === 0 && (
                      <div className="text-center py-10 px-4 bg-slate-50/50 dark:bg-white/[0.01] rounded-2xl border border-dashed border-slate-200 dark:border-white/10">
                        <GraduationCap className="h-10 w-10 mx-auto text-slate-300 dark:text-zinc-600 mb-2" />
                        <p className="text-xs font-bold text-slate-700 dark:text-zinc-300">
                          {busquedaCandidato 
                            ? "No se encontraron alumnos disponibles que coincidan con la búsqueda." 
                            : "Todos los alumnos registrados ya se encuentran matriculados en este grupo."}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1">
                          Puedes registrar más alumnos en la pestaña "Miembros" o cargar una lista masiva con Excel.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PESTAÑA 2: LISTA DE ALUMNOS EN EL SALÓN */}
              {pestanaGrupo === 'miembros' && (
                <div className="space-y-4 animate-fadeIn">
                  
                  {/* Barra de Filtro y Acciones del Salón */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05]">
                    
                    {/* Buscador dentro del Grupo */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                      <input
                        type="text"
                        placeholder="Filtrar por nombre, apellido o correo..."
                        className="w-full pl-10 pr-4 py-2 rounded-xl text-xs font-medium bg-white dark:bg-[#141414] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500/30 outline-none transition-all"
                        value={busquedaMiembroGrupo}
                        onChange={(e) => setBusquedaMiembroGrupo(e.target.value)}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      {seleccionadosParaRemover.length > 0 && (
                        <button
                          type="button"
                          onClick={removerSeleccionadosDelGrupo}
                          disabled={removiendoLote}
                          className="px-3.5 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all active:scale-95 cursor-pointer"
                        >
                          <Trash2 size={14} />
                          <span>Remover ({seleccionadosParaRemover.length})</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={exportarListaGrupoCSV}
                        disabled={miembrosGrupo.length === 0}
                        className="px-3.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-200 text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all disabled:opacity-40 cursor-pointer"
                      >
                        <Download size={14} />
                        <span>Exportar CSV</span>
                      </button>
                    </div>
                  </div>

                  {/* Acciones de selección de remoción */}
                  {miembrosGrupoFiltrados.length > 0 && (
                    <div className="flex items-center justify-between px-2 text-xs">
                      <button
                        type="button"
                        onClick={alternarSeleccionarTodosRemover}
                        className="flex items-center gap-2 text-slate-500 hover:text-slate-800 dark:hover:text-white font-bold transition-colors cursor-pointer"
                      >
                        {todosMiembrosGrupoVisiblesSeleccionados ? (
                          <CheckSquare className="h-4 w-4 text-indigo-600" />
                        ) : (
                          <Square className="h-4 w-4 text-slate-400" />
                        )}
                        <span>Seleccionar todos para remover</span>
                      </button>

                      <span className="text-[11px] text-slate-400 font-mono">
                        {miembrosGrupoFiltrados.length} alumnos en lista
                      </span>
                    </div>
                  )}

                  {/* Listado de Miembros Matriculados */}
                  {cargandoMiembrosGrupo ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent"></div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                      {miembrosGrupoFiltrados.map(mg => {
                        const estaSeleccionado = seleccionadosParaRemover.includes(mg.id_miembro)
                        return (
                          <div 
                            key={mg.id_grupo_miembro} 
                            className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all ${
                              estaSeleccionado
                                ? 'bg-rose-50/60 dark:bg-rose-950/20 border-rose-300 dark:border-rose-500/30'
                                : 'bg-slate-50/80 dark:bg-white/[0.02] hover:bg-slate-100/60 dark:hover:bg-white/[0.04] border-slate-100 dark:border-white/[0.04]'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                onClick={() => alternarSeleccionRemover(mg.id_miembro)}
                                className="cursor-pointer text-rose-500"
                              >
                                {estaSeleccionado ? (
                                  <CheckSquare className="h-4 w-4" />
                                ) : (
                                  <Square className="h-4 w-4 text-slate-300 dark:text-zinc-600" />
                                )}
                              </div>

                              {mg.avatar ? (
                                <img 
                                  src={mg.avatar} 
                                  alt={`${mg.nombre} ${mg.apellido}`} 
                                  className="w-8 h-8 rounded-full object-cover shadow-sm border border-slate-200 dark:border-white/10" 
                                  referrerPolicy="no-referrer" 
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-500 text-white font-extrabold text-[11px] flex items-center justify-center shadow-sm">
                                  {(mg.nombre || 'U').charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <p className="text-xs font-bold text-slate-900 dark:text-white">{mg.nombre} {mg.apellido}</p>
                                <p className="text-[11px] text-slate-400 font-mono">{mg.email}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase font-mono bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10">
                                {mg.rol_miembro}
                              </span>
                              <button 
                                onClick={() => eliminarMiembroDeGrupo(mg.id_miembro)}
                                className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl transition-all cursor-pointer"
                                title="Remover del salón"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )
                      })}

                      {miembrosGrupo.length === 0 && (
                        <div className="text-center py-10 px-4 bg-slate-50/50 dark:bg-white/[0.01] rounded-2xl border border-dashed border-slate-200 dark:border-white/10">
                          <UserGroupIcon className="h-10 w-10 mx-auto text-slate-300 dark:text-zinc-600 mb-2" />
                          <p className="text-xs font-bold text-slate-700 dark:text-zinc-300">
                            Este grupo aún no tiene alumnos asignados.
                          </p>
                          <p className="text-[11px] text-slate-400 mt-1 mb-4">
                            Usa la pestaña "Matricular Alumnos" para seleccionar y añadir a tus estudiantes.
                          </p>
                          <button
                            type="button"
                            onClick={() => setPestanaGrupo('matricular')}
                            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md shadow-indigo-500/25 transition-all inline-flex items-center gap-1.5 cursor-pointer"
                          >
                            <UserPlus size={14} />
                            <span>Ir a Matricular Alumnos</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-white/[0.06] flex items-center justify-between bg-slate-50/30 dark:bg-white/[0.01]">
              <div className="text-[11px] text-slate-400 font-medium">
                {miembrosGrupo.length} alumno(s) inscritos en {grupoSeleccionado.nombre}
              </div>
              <button
                onClick={cerrarGestionGrupo}
                className="px-5 py-2.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 transition-all cursor-pointer"
              >
                Cerrar Panel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Grupo (Bento Glassmorphic) */}
      {mostrarModalEditarGrupo && grupoAEditar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fadeIn">
          <div className="bg-white/95 dark:bg-[#0c0c0c]/95 rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-2xl w-full max-w-md p-6 sm:p-8 overflow-hidden relative">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                  <PencilSquareIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">Editar Grupo</h3>
                  <p className="text-xs text-slate-400 font-medium">Modifica nombre y descripción académica</p>
                </div>
              </div>
              <button 
                onClick={() => setMostrarModalEditarGrupo(false)} 
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="institucion-editar-grupo-nombre" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Nombre del Grupo *</label>
                <input 
                  id="institucion-editar-grupo-nombre"
                  name="grupoAEditar.nombre"
                  type="text" 
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  value={grupoAEditar.nombre}
                  onChange={(e) => setGrupoAEditar({...grupoAEditar, nombre: e.target.value})}
                />
              </div>

              <div>
                <label htmlFor="institucion-editar-grupo-descripcion" className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 uppercase tracking-wider">Descripción</label>
                <textarea 
                  id="institucion-editar-grupo-descripcion"
                  name="grupoAEditar.descripcion"
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  value={grupoAEditar.descripcion || ''}
                  onChange={(e) => setGrupoAEditar({...grupoAEditar, descripcion: e.target.value})}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-white/[0.06]">
                <button 
                  onClick={() => setMostrarModalEditarGrupo(false)}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                >
                  Cancelar
                </button>
                <button 
                  onClick={guardarEdicionGrupo}
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-md shadow-indigo-500/25 transition-all"
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Importar Excel (Bento Drag & Drop) */}
      {mostrarModalImportar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fadeIn">
          <div className="bg-white/95 dark:bg-[#0c0c0c]/95 rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-2xl w-full max-w-lg p-6 sm:p-8 overflow-hidden relative">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <DocumentArrowUpIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">Carga Masiva desde Excel / CSV</h3>
                  <p className="text-xs text-slate-400 font-medium">Importa listas de alumnos y operadores</p>
                </div>
              </div>
              <button 
                onClick={() => setMostrarModalImportar(false)} 
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              {/* Tarjeta de Descarga de Plantilla */}
              <div className="p-3.5 rounded-2xl bg-emerald-50/60 dark:bg-emerald-500/5 border border-emerald-200/60 dark:border-emerald-500/20 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300">¿Requieres el formato oficial?</p>
                  <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/80">Columnas requeridas: Correo, Nombre, Apellido</p>
                </div>
                <button
                  type="button"
                  onClick={descargarPlantillaExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-white/10 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30 text-xs font-bold shadow-sm hover:bg-emerald-50 transition-all"
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  <span>Plantilla CSV</span>
                </button>
              </div>

              {/* Zona Drag & Drop */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDraggingExcel(true); }}
                onDragLeave={() => setIsDraggingExcel(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingExcel(false);
                  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                    setArchivoExcel(e.dataTransfer.files[0]);
                  }
                }}
                className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all ${
                  isDraggingExcel
                    ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-500/10 scale-[1.01]'
                    : archivoExcel
                    ? 'border-emerald-500/50 bg-emerald-50/30 dark:bg-emerald-500/5'
                    : 'border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] hover:border-emerald-500/40'
                }`}
              >
                <input
                  type="file"
                  id="excel-file-input"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setArchivoExcel(e.target.files ? e.target.files[0] : null)}
                  className="hidden"
                />

                {archivoExcel ? (
                  <div className="flex flex-col items-center">
                    <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 mb-2">
                      <CheckCircleIcon className="h-8 w-8" />
                    </div>
                    <p className="text-xs font-bold text-slate-900 dark:text-white font-mono">{archivoExcel.name}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{(archivoExcel.size / 1024).toFixed(1)} KB</p>
                    <button
                      type="button"
                      onClick={() => setArchivoExcel(null)}
                      className="mt-3 text-[11px] font-bold text-rose-500 hover:text-rose-600 transition-colors"
                    >
                      Cambiar archivo
                    </button>
                  </div>
                ) : (
                  <label htmlFor="excel-file-input" className="cursor-pointer block">
                    <DocumentArrowUpIcon className="h-10 w-10 mx-auto text-slate-400 dark:text-zinc-500 mb-2" />
                    <p className="text-xs font-bold text-slate-700 dark:text-zinc-300">
                      Arrastra tu archivo aquí o <span className="text-emerald-600 dark:text-emerald-400 underline">haz clic para explorar</span>
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase font-semibold">Formatos compatibles: .xlsx, .xls, .csv</p>
                  </label>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-white/[0.06]">
                <button 
                  onClick={() => { setMostrarModalImportar(false); setArchivoExcel(null); }}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleImportarExcel}
                  disabled={!archivoExcel || importandoExcel}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/25 transition-all flex items-center gap-2"
                >
                  {importandoExcel ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/20 border-t-white" />
                      <span>Importando datos...</span>
                    </>
                  ) : (
                    <span>Procesar e Importar</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaginaGestionMiembros
